/* ============================================================
   AVORA HOME — Fly-to-Cart Animation
   4-phase animation: Gather → Pack → Fly → Cart Bounce
   Vanilla JS, no external libraries.
   ============================================================ */

(function() {
  'use strict';

  var ANIMATION_DURATION = 1800; // total ms
  var isAnimating = false;

  /**
   * Main entry point — call from any add-to-cart trigger.
   * @param {Object} opts
   *   - imageEl:   DOM element of the product image (for position)
   *   - imageSrc:  URL of product image
   *   - title:     Product title string
   *   - price:     Formatted price string
   *   - sourceEl:  The button/element that was clicked (for position fallback)
   *   - onComplete: Optional callback when animation finishes
   */
  window.avoraFlyToCart = function(opts) {
    if (!opts || !opts.imageSrc) return;
    if (isAnimating) {
      // Queue: just fire the cart open after delay
      setTimeout(function() {
        if (window.avoraCartDrawer) window.avoraCartDrawer.open();
      }, 600);
      return;
    }

    isAnimating = true;

    var cartIcon = findCartIcon();
    if (!cartIcon) {
      isAnimating = false;
      // No cart icon found, just open drawer
      setTimeout(function() {
        if (window.avoraCartDrawer) window.avoraCartDrawer.open();
      }, 500);
      return;
    }

    // Get positions
    var sourceRect = getSourceRect(opts);
    var cartRect = cartIcon.getBoundingClientRect();

    // Create animation container
    var overlay = document.createElement('div');
    overlay.className = 'avora-fly-overlay';
    overlay.setAttribute('aria-hidden', 'true');
    document.body.appendChild(overlay);

    // Phase 1: Gather — create floating image clone
    var floater = createFloater(opts, sourceRect);
    overlay.appendChild(floater);

    // Animate: Gather (0-400ms) → Pack (400-800ms) → Fly (800-1500ms) → Bounce (1500-1800ms)
    requestAnimationFrame(function() {
      phaseGather(floater, sourceRect, function() {
        phasePack(floater, opts, function() {
          phaseFly(floater, cartRect, function() {
            phaseCartBounce(cartIcon, function() {
              // Cleanup
              if (overlay.parentNode) overlay.parentNode.removeChild(overlay);
              isAnimating = false;

              // Auto-open cart drawer after 500ms
              setTimeout(function() {
                if (window.avoraCartDrawer) window.avoraCartDrawer.open();
              }, 500);

              if (opts.onComplete) opts.onComplete();
            });
          });
        });
      });
    });
  };

  /**
   * Fly animation for multiple items (e.g. bundle add-all).
   * Staggers each product by 200ms.
   */
  window.avoraFlyToCartMultiple = function(items) {
    if (!items || !items.length) return;

    items.forEach(function(item, index) {
      setTimeout(function() {
        window.avoraFlyToCart(item);
      }, index * 200);
    });
  };

  /* ----------------------------------------------------------
     HELPERS
     ---------------------------------------------------------- */

  function findCartIcon() {
    // Try multiple selectors to find the cart icon in the header
    return document.querySelector('cart-icon') ||
           document.querySelector('.action__cart') ||
           document.querySelector('[data-testid="cart-drawer-trigger"]') ||
           document.querySelector('.header-actions__cart-icon') ||
           document.querySelector('a[href="/cart"]');
  }

  function getSourceRect(opts) {
    if (opts.imageEl) {
      return opts.imageEl.getBoundingClientRect();
    }
    if (opts.sourceEl) {
      return opts.sourceEl.getBoundingClientRect();
    }
    // Fallback: center of screen
    return {
      left: window.innerWidth / 2 - 40,
      top: window.innerHeight / 2 - 40,
      width: 80,
      height: 80
    };
  }

  function createFloater(opts, sourceRect) {
    var el = document.createElement('div');
    el.className = 'avora-fly-floater';

    // Product image thumbnail
    var img = document.createElement('img');
    img.src = opts.imageSrc;
    img.alt = '';
    img.className = 'avora-fly-floater__image';
    el.appendChild(img);

    // Position at source
    el.style.left = (sourceRect.left + sourceRect.width / 2 - 40) + 'px';
    el.style.top = (sourceRect.top + sourceRect.height / 2 - 40) + 'px';
    el.style.width = '80px';
    el.style.height = '80px';

    return el;
  }

  /* ----------------------------------------------------------
     ANIMATION PHASES
     ---------------------------------------------------------- */

  function phaseGather(floater, sourceRect, cb) {
    // Move to center of viewport, scale down slightly
    var centerX = window.innerWidth / 2 - 40;
    var centerY = window.innerHeight / 2 - 30;

    floater.animate([
      {
        left: floater.style.left,
        top: floater.style.top,
        opacity: 0.6,
        transform: 'scale(1)',
        borderRadius: '12px'
      },
      {
        left: centerX + 'px',
        top: centerY + 'px',
        opacity: 1,
        transform: 'scale(0.85)',
        borderRadius: '12px'
      }
    ], {
      duration: 350,
      easing: 'cubic-bezier(0.34, 1.56, 0.64, 1)',
      fill: 'forwards'
    });

    setTimeout(cb, 350);
  }

  function phasePack(floater, opts, cb) {
    // Expand into a glass-morphic package card
    var centerX = window.innerWidth / 2 - 80;
    var centerY = window.innerHeight / 2 - 30;

    // Add text content
    var infoWrap = document.createElement('div');
    infoWrap.className = 'avora-fly-floater__info';
    infoWrap.innerHTML =
      '<span class="avora-fly-floater__title">' + escapeText(opts.title || '').substring(0, 30) + '</span>' +
      '<span class="avora-fly-floater__price">' + escapeText(opts.price || '') + '</span>';
    floater.appendChild(infoWrap);

    floater.animate([
      {
        width: '80px',
        height: '80px',
        left: (window.innerWidth / 2 - 40) + 'px',
        top: (window.innerHeight / 2 - 30) + 'px',
        borderRadius: '12px'
      },
      {
        width: '180px',
        height: '60px',
        left: centerX + 'px',
        top: centerY + 'px',
        borderRadius: '14px'
      }
    ], {
      duration: 350,
      easing: 'cubic-bezier(0.25, 0.1, 0.25, 1)',
      fill: 'forwards'
    });

    // Animate the glass border drawing
    floater.classList.add('avora-fly-floater--packed');

    setTimeout(cb, 400);
  }

  function phaseFly(floater, cartRect, cb) {
    // Fly along a curved bezier arc to the cart icon
    var cartX = cartRect.left + cartRect.width / 2 - 10;
    var cartY = cartRect.top + cartRect.height / 2 - 10;

    // Current position (center of viewport)
    var startX = window.innerWidth / 2 - 80;
    var startY = window.innerHeight / 2 - 30;

    // Calculate arc control point (above and to the right for a nice curve)
    var cpX = startX + (cartX - startX) * 0.5;
    var cpY = Math.min(startY, cartY) - 120;

    // Use Web Animations API with keyframes that simulate the arc
    var steps = 30;
    var keyframes = [];

    for (var i = 0; i <= steps; i++) {
      var t = i / steps;
      // Quadratic bezier: B(t) = (1-t)²·P0 + 2(1-t)t·CP + t²·P1
      var x = (1 - t) * (1 - t) * startX + 2 * (1 - t) * t * cpX + t * t * cartX;
      var y = (1 - t) * (1 - t) * startY + 2 * (1 - t) * t * cpY + t * t * cartY;

      var scale = 1 - t * 0.7; // 1 → 0.3
      var opacity = t > 0.8 ? 1 - ((t - 0.8) / 0.2) : 1; // fade in last 20%

      keyframes.push({
        left: x + 'px',
        top: y + 'px',
        transform: 'scale(' + scale + ')',
        opacity: opacity,
        width: (180 - t * 160) + 'px',
        height: (60 - t * 40) + 'px'
      });
    }

    floater.animate(keyframes, {
      duration: 650,
      easing: 'cubic-bezier(0.16, 1, 0.3, 1)',
      fill: 'forwards'
    });

    setTimeout(cb, 650);
  }

  function phaseCartBounce(cartIcon, cb) {
    // Bounce the cart icon
    cartIcon.animate([
      { transform: 'scale(1)' },
      { transform: 'scale(1.3)' },
      { transform: 'scale(0.95)' },
      { transform: 'scale(1.1)' },
      { transform: 'scale(1)' }
    ], {
      duration: 400,
      easing: 'cubic-bezier(0.34, 1.56, 0.64, 1)',
      fill: 'forwards'
    });

    // Add ripple pulse around cart icon
    var ripple = document.createElement('span');
    ripple.className = 'avora-cart-ripple';
    var iconRect = cartIcon.getBoundingClientRect();
    ripple.style.left = (iconRect.left + iconRect.width / 2) + 'px';
    ripple.style.top = (iconRect.top + iconRect.height / 2) + 'px';
    document.body.appendChild(ripple);

    // Glow the cart bubble briefly
    var bubble = cartIcon.querySelector('.cart-bubble, .cart-bubble__background');
    if (bubble) {
      bubble.style.transition = 'box-shadow 0.3s ease';
      bubble.style.boxShadow = '0 0 12px 4px rgba(196, 168, 130, 0.5)';
      setTimeout(function() {
        bubble.style.boxShadow = '';
      }, 500);
    }

    setTimeout(function() {
      if (ripple.parentNode) ripple.parentNode.removeChild(ripple);
      cb();
    }, 500);
  }

  function escapeText(str) {
    if (!str) return '';
    var div = document.createElement('div');
    div.appendChild(document.createTextNode(str));
    return div.innerHTML;
  }

})();
